/********************************************************************
 * @author:      Kaven
 * @email:       kaven@wuwenkai.com
 * @website:     http://blog.kaven.xyz
 * @file:        [Kaven-Basic] /src/libs/core/String2ArrayBuffer.ts
 * @create:      2025-07-01 13:21:31.491
 * @modify:      2025-07-11 21:19:12.958
 * @version:     6.0.0
 * @times:       7
 * @lines:       76
 * @copyright:   Copyright © 2018-2025 Kaven. All Rights Reserved.
 * @description: [description]
 * @license:     
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 * 
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 * 
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 ********************************************************************/

/**
 * Converts a string into an ArrayBuffer where each character is represented by its UTF-16 code unit (lower 8 bits).
 *
 * @param str - The input string to convert.
 * @returns An ArrayBuffer containing the UTF-8 encoded bytes of the input string.
 *
 * @remarks
 * This function encodes each character of the string as a single byte, using the character's char code.
 * Characters with code points above 255 will be truncated to fit into a single byte.
 * For full Unicode support, consider using TextEncoder instead.
 * 
 * @see https://developers.google.com/web/updates/2012/06/How-to-convert-ArrayBuffer-to-and-from-String
 * 
 * @since 4.0.0
 * @version 2021-12-11
 */
export function String2ArrayBuffer(str: string) {
    const buf = new ArrayBuffer(str.length);
    const bufView = new Uint8Array(buf);
    for (let i = 0, strLen = str.length; i < strLen; i++) {
        bufView[i] = str.charCodeAt(i);
    }
    return buf;
}

/**
 * Converts an ArrayBuffer or ArrayBuffer-like object to a string using UTF-16 code units.
 *
 * @param buf - The ArrayBuffer or ArrayBuffer-like object to convert.
 * @returns The resulting string representation of the buffer.
 *
 * @remarks
 * This function interprets each byte in the buffer as a single UTF-16 code unit.
 * For multi-byte encoded strings (e.g., UTF-8), consider using a `TextDecoder` instead.
 * 
 * @see https://developers.google.com/web/updates/2012/06/How-to-convert-ArrayBuffer-to-and-from-String
 * 
 * @since 4.0.0
 * @version 2021-12-11
 */
export function ArrayBuffer2String(buf: ArrayBufferLike) {
    return String.fromCharCode.apply(null, Array.from(new Uint8Array(buf)));
}
